import { Component, OnDestroy, DoCheck, Input, HostBinding, ElementRef, Optional, Self } from '@angular/core';
import { MatFormFieldControl } from '@angular/material/form-field';
import { ControlValueAccessor, UntypedFormControl, NgControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { FocusMonitor } from '@angular/cdk/a11y';
import { OrganizationalStructure, OrganizationalStructureService } from '@core/api';
import { SharedModule } from '@shared/modules';

@Component({
  selector: 'net-select-organizational-structure',
  templateUrl: './select-organizational-structure.component.html',
  styleUrls: ['./select-organizational-structure.component.scss'],
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: SelectOrganizationalStructureComponent
    }
  ],
  standalone: true,
  imports: [
    SharedModule
  ]
})
export class SelectOrganizationalStructureComponent implements OnDestroy, DoCheck, ControlValueAccessor, MatFormFieldControl<OrganizationalStructure[]> {

  static nextId = 0;

  loading = false;
  focused = false;
  errorState = false;
  controlType = 'select-organizational-structure';
  describedBy = '';
  stateChanges = new Subject<void>();

  organizationalStructures: OrganizationalStructure[] = [];
  selectCtrl = new UntypedFormControl();

  @Input() tabIndex;

  @Input()
  get salesOrganizationId() { return this._salesOrganizationId; }
  set salesOrganizationId(value) {
    if (value && this.salesOrganizationId !== value) {
      this.fetchList(value);
    }
    this._salesOrganizationId = value;
  }
  private _salesOrganizationId: string;

  @Input()
  get placeholder(): string { return this._placeholder; }
  set placeholder(value: string) {
    this._placeholder = value;
    this.stateChanges.next();
  }
  private _placeholder: string;

  @Input()
  get required(): boolean { return this._required; }
  set required(value: boolean) {
    this._required = coerceBooleanProperty(value);
    this.stateChanges.next();
  }
  private _required = false;

  @Input()
  get disabled(): boolean { return this._disabled; }
  set disabled(value: boolean) {
    if (value) {
      this.selectCtrl.disable();
    } else {
      this.selectCtrl.enable();
    }
    this._disabled = value;
    this.stateChanges.next();
  }
  private _disabled = false;

  @Input()
  get value(): OrganizationalStructure[] { return this.selectCtrl.value; }
  set value(value: OrganizationalStructure[]) {
    this.selectCtrl.setValue(value);
    this.onSelectionChange();
  }

  @Input() multiple = true;

  @HostBinding('id') id = `select-organizational-structure-${SelectOrganizationalStructureComponent.nextId++}`;
  @HostBinding('attr.aria-describedby') describedByBinding = this.describedBy;

  get shouldLabelFloat() { return this.focused || !this.empty; }

  get empty() { return !this.value; }

  constructor(
    private _focusMonitor: FocusMonitor,
    private _elementRef: ElementRef<HTMLElement>,
    private organizationalStructureService: OrganizationalStructureService,
    @Optional() @Self() public ngControl: NgControl
  ) {
    _focusMonitor.monitor(_elementRef, true).subscribe(origin => {
      if (this.disabled) {
        return;
      }

      if (this.focused && !origin) {
        this.onTouched();
      }
      this.focused = !!origin;
      this.stateChanges.next();
    });

    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }

  private fetchList(salesOrganizationId: string) {
    this.loading = true;
    const request = { filter: { salesOrganizationId } };

    this.organizationalStructureService.search(request).subscribe(response => {
      this.loading = false;
      this.organizationalStructures = response.data.results;

      this.onSelectionChange();
    });
  }

  onSelectionChange() {
    this.onChange(this.value);
    this.selectionChange(this.value);
    this.stateChanges.next();
  }

  selectAll() {
    this.selectCtrl.patchValue(this.organizationalStructures);
    this.onSelectionChange();
  }

  deselectAll() {
    this.selectCtrl.patchValue([]);
    this.onSelectionChange();
  }

  compareFn(first: OrganizationalStructure, second: OrganizationalStructure) {
    return first && second ? (first.organizationalStructureId === second.organizationalStructureId || first.level === second.level)
      : first === second;
  }

  onChange = (_: any) => { };
  selectionChange = (_: any) => { };
  onTouched = () => { };
  onContainerClick = () => { };
  registerOnChange = (fn: any) => this.onChange = fn;
  registerOnTouched = (fn: any) => this.onTouched = fn;
  setDisabledState = (isDisabled: boolean) => this.disabled = isDisabled;
  setDescribedByIds = (ids: string[]) => this.describedBy = ids.join(' ');
  writeValue = (value: OrganizationalStructure[]) => {
    this.value = value;
    this.selectCtrl.setValue(value);
  }

  ngOnDestroy = () => this.stateChanges.complete();

  ngDoCheck() {
    // Reflect control valid status for mat form field error state
    if (this.ngControl) {
      this.errorState = this.ngControl.invalid && this.ngControl.touched;
      this.stateChanges.next();
    }
  }
}
